import { useCallback, useEffect, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  countdownWords,
  localInputStamp,
  refusalReason,
  stampWithZone,
  type Capability,
  type Exposure,
  type Incident,
  type IncidentClocks,
  type IncidentDraft,
  type Issue,
  type Vendor,
} from '../lib/api'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { ExposureAnswer, ExposureLoadError, TICK_MS, useNow } from '../components/VendorExposure'
import { Status, Toggletip, type Tone } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { Crumbs } from '../components/Crumbs'
import '../components/kit/back.css'
import { VendorChoice } from '../components/VendorChoice'
import '../styles/sweep.css'

// The incident register, and the answer one incident produces.
//
// WHY THE REGISTER CARRIES A DUTY PER ROW
//
// It is opened during an incident, not to audit one afterwards. The only thing
// worth reading across the rows is which duty runs out first, so each live row
// carries it. Anything less is a list of references somebody then opens one at
// a time while a clock runs.
//
// The API works those out on request rather than by default, because each row
// is a traversal of that vendor's declared facts, and it stops after the first
// twenty five; a row past that says so in its own cell. So the live list asks
// for clocks and the closed list does not: nothing is owed on an incident that
// concluded months ago, and spending a traversal on it would push a live row
// past the cap.
//
// WHY THE LIVE AND CLOSED LISTS ARE TWO READS
//
// The API answers the live register by default and the whole thing on request.
// Reading everything once and splitting here would spend the clock budget on
// closed rows before reaching the open ones, which is the wrong twenty five.

// Which capability records and changes an incident. finding.manage, matching
// the route: recording an incident, taking it and closing it with words is the
// same act as raising, owning and closing a finding, and the roles that hold it
// are the bench that answers an incident call. It is named once here so that a
// disabled control and the 403 behind it cannot drift apart.
const HANDLE_INCIDENT: Capability = 'finding.manage'

const SOURCES: { value: string; label: string }[] = [
  { value: 'VENDOR_NOTIFIED', label: 'The vendor told us' },
  { value: 'INTERNAL', label: 'We found it ourselves' },
  { value: 'PUBLIC', label: 'It was public before we knew' },
  { value: 'REGULATOR', label: 'A regulator told us' },
]

function sourceWords(code: string): string {
  return SOURCES.find((s) => s.value === code)?.label ?? code
}

const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']

// What each status means, in the words the API uses for it.
const STATUSES: { value: string; label: string; help: string }[] = [
  { value: 'OPEN', label: 'Open', help: 'Still running' },
  { value: 'CONTAINED', label: 'Contained', help: 'Stopped and not concluded, no statutory clock stops here' },
  { value: 'CLOSED', label: 'Closed', help: 'Concluded in writing, the closure note is what an auditor reads first' },
]

// A running incident is the loud one; a contained one still owes its clocks,
// so it keeps a warning; a closed one is finished and goes quiet.
const STATUS_TONE: Record<string, Tone> = {
  OPEN: 'crit',
  CONTAINED: 'warn',
  CLOSED: 'plain',
}

function IncidentStatus({ status }: { status: string }): JSX.Element {
  const word = STATUSES.find((s) => s.value === status)?.label ?? status.charAt(0) + status.slice(1).toLowerCase()
  return <Status tone={STATUS_TONE[status] ?? 'neutral'}>{word}</Status>
}

// The API's notes lead with a short sentence ("Contained.", "Nothing
// changed.") and then explain. The lead stays on the line as written and the
// whole note opens from the (i), so nothing it says is cut or reworded.
function firstSentence(text: string): string {
  const at = text.indexOf('. ')
  return at < 0 ? text : text.slice(0, at + 1)
}

function NoteLine({ text, label }: { text: string; label: string }): JSX.Element {
  const lead = firstSentence(text)
  return (
    <>
      <span>{lead}</span>
      {lead !== text && <ReasonTip label={label} text={text} />}
    </>
  )
}

type Draft = {
  vendorId: string
  discoveredAt: string
  beganAt: string
  source: string
  summary: string
  severity: string
  handledBy: string
}

function emptyDraft(): Draft {
  return {
    vendorId: '',
    discoveredAt: localInputStamp(new Date()),
    beganAt: '',
    source: 'VENDOR_NOTIFIED',
    summary: '',
    severity: '',
    handledBy: '',
  }
}

// The field names match the ones the API's own refusal carries, so a check made
// here and a 422 from the server land under the same control. Every rule below
// is one the API or migration 0031 enforces anyway; they are repeated because a
// refusal arriving after the form was submitted reads as a system failure
// rather than as a typo in a time.
function checkDraft(d: Draft): Issue[] {
  const out: Issue[] = []
  if (!d.vendorId) {
    out.push({ field: 'vendorId', code: 'REQUIRED', detail: 'An incident belongs to a vendor' })
  }
  if (!d.discoveredAt) {
    out.push({
      field: 'discoveredAt',
      code: 'REQUIRED',
      detail: 'Say when this was noticed, every statutory window counts from that moment',
    })
  } else if (new Date(d.discoveredAt).getTime() > Date.now() + 60000) {
    out.push({
      field: 'discoveredAt',
      code: 'IN_THE_FUTURE',
      detail: 'That is later than now, and a clock cannot count from a moment still to come',
    })
  }
  if (d.beganAt && d.discoveredAt && new Date(d.beganAt).getTime() > new Date(d.discoveredAt).getTime()) {
    out.push({
      field: 'beganAt',
      code: 'AFTER_DISCOVERY',
      detail: 'This begins after it was noticed, so one of the two times is a typo',
    })
  }
  if (!d.source) {
    out.push({ field: 'source', code: 'REQUIRED', detail: 'How this reached us decides what can be relied on later' })
  }
  return out
}

const ANCHOR: Record<string, string> = {
  vendorId: 'inc-vendor',
  discoveredAt: 'inc-discovered',
  beganAt: 'inc-began',
  source: 'inc-source',
  summary: 'inc-summary',
  severity: 'inc-severity',
  handledBy: 'inc-handler',
}

// The console routes on the fragment, so href="#id" would change the route
// rather than move to the field. Focus is moved by hand and the fragment left
// alone.
function focusField(id: string): void {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ block: 'center' })
  el.focus()
}

export function Incidents({ incidentId }: { incidentId?: string }): JSX.Element {
  if (incidentId) return <IncidentAnswer incidentId={incidentId} />
  return <IncidentRegister />
}

// ------------------------------------------------------------ the register

// One page of closed incidents. It is a page and not a total, and the figure
// printed from it says so once it is full, because a register that reports
// "3 closed" when there are ninety is worse than one that reports nothing.
const CLOSED_PAGE = 100

function IncidentRegister(): JSX.Element {
  // The register is left open on a desk during an incident. A countdown worked
  // out once at render keeps claiming the time that was left when somebody
  // loaded it, which is the one number on this screen that has to be true.
  const now = useNow(TICK_MS)
  const [live, setLive] = useState<Incident[] | null>(null)
  const [closed, setClosed] = useState<Incident[]>([])
  const [error, setError] = useState<unknown>(null)
  const [vendors, setVendors] = useState<Vendor[]>([])

  const [formOpen, setFormOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [issues, setIssues] = useState<Issue[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<ApiError | null>(null)

  const load = useCallback(() => {
    api
      .incidents({ withClocks: true, limit: 100 })
      .then((page) => {
        setLive(page.incidents)
        setError(null)
      })
      .catch((err) => {
        setLive(null)
        setError(err)
      })
    api
      .incidents({ status: ['CLOSED'], limit: CLOSED_PAGE })
      .then((page) => setClosed(page.incidents))
      .catch(() => setClosed([]))
  }, [])

  useEffect(() => {
    load()
    api
      .vendors({})
      .then((p) => setVendors(p.vendors))
      .catch(() => setVendors([]))
  }, [load])

  const mayHandle = can(HANDLE_INCIDENT)

  function submit(ev: FormEvent) {
    ev.preventDefault()
    const found = checkDraft(draft)
    setIssues(found)
    setSubmitError(null)
    if (found.length > 0) {
      const first = found[0]
      if (first) focusField(ANCHOR[first.field] ?? 'inc-vendor')
      return
    }
    const body: IncidentDraft = {
      discoveredAt: new Date(draft.discoveredAt).toISOString(),
      source: draft.source,
    }
    if (draft.beganAt) body.beganAt = new Date(draft.beganAt).toISOString()
    if (draft.summary.trim()) body.summary = draft.summary.trim()
    if (draft.severity) body.severity = draft.severity
    if (draft.handledBy.trim()) body.handledBy = draft.handledBy.trim()

    setSubmitting(true)
    api
      .recordIncident(draft.vendorId, body)
      .then((reply) => {
        setFormOpen(false)
        setDraft(emptyDraft())
        // Straight to the answer. Somebody who has just recorded an incident is
        // not looking for confirmation that a row exists; they are looking for
        // the six hour clock.
        window.location.hash = '#/incident/' + encodeURIComponent(reply.incident.id)
      })
      .catch((err) => {
        setSubmitError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err)))
        const first = err instanceof ApiError ? (err.issues[0]?.field ?? err.fields[0]) : undefined
        const anchor = first ? ANCHOR[first] : undefined
        if (anchor) focusField(anchor)
      })
      .finally(() => setSubmitting(false))
  }

  const issueFor = (field: string) => issues.find((i) => i.field === field)

  if (error != null) return <ErrorBox error={error} what="The incident register" />
  if (live === null) return <Loading what="the incident register" />

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0 }}>Incidents</h1>
          <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
            What a vendor incident exposes, and who has to be told by when
          </p>
        </div>
        <button
          type="button"
          className="primary"
          onClick={() => setFormOpen((v) => !v)}
          aria-expanded={formOpen}
          disabled={!mayHandle}
          title={mayHandle ? undefined : refusalReason(HANDLE_INCIDENT)}
        >
          Record an incident
        </button>
      </div>

      {/* The refusal names the roles that hold the act and can run long, so it
          opens from the (i) beside the one line that says what it means here. */}
      {!mayHandle && (
        <p className="note sw-row" role="status">
          <Status tone="neutral">Read only</Status>
          <span>You can read this register but not add to it</span>
          <ReasonTip label="Why you cannot record an incident" text={refusalReason(HANDLE_INCIDENT)} />
        </p>
      )}

      {formOpen && mayHandle && (
        <section className="panel" aria-labelledby="inc-form-head">
          <h2 id="inc-form-head" style={{ marginTop: 0 }}>
            Record an incident
          </h2>

          {issues.length > 0 && (
            <div className="form-errors" role="alert">
              <h3>This cannot be recorded yet</h3>
              <ol>
                {issues.map((i) => (
                  <li key={i.field + i.code}>
                    <button
                      type="button"
                      className="linkish"
                      onClick={() => focusField(ANCHOR[i.field] ?? 'inc-vendor')}
                    >
                      {i.detail}
                    </button>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {submitError && <ErrorBox error={submitError} what="The incident" verb="recorded" />}

          <form onSubmit={submit}>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="inc-vendor">Vendor</label>
                <VendorChoice
                  bare
                  browse
                  id="inc-vendor"
                  label="Vendor"
                  value={draft.vendorId}
                  options={vendors}
                  placeholder="The supplier this is about: name or code"
                  invalid={Boolean(issueFor('vendorId'))}
                  describedBy={issueFor('vendorId') ? 'inc-vendor-issue' : undefined}
                  onChange={(id) => setDraft({ ...draft, vendorId: id })}
                />
                {issueFor('vendorId') && (
                  <span className="field-error" id="inc-vendor-issue">
                    {issueFor('vendorId')?.detail}
                  </span>
                )}
              </div>

              <div className="field">
                <label htmlFor="inc-discovered">Noticed at</label>
                <input
                  id="inc-discovered"
                  type="datetime-local"
                  value={draft.discoveredAt}
                  aria-describedby="inc-discovered-hint"
                  onChange={(e) => setDraft({ ...draft, discoveredAt: e.target.value })}
                />
                {issueFor('discoveredAt') && <span className="field-error">{issueFor('discoveredAt')?.detail}</span>}
              </div>

              <div className="field">
                <label htmlFor="inc-began">Believed to have begun</label>
                <input
                  id="inc-began"
                  type="datetime-local"
                  value={draft.beganAt}
                  aria-describedby="inc-began-hint"
                  onChange={(e) => setDraft({ ...draft, beganAt: e.target.value })}
                />
                {issueFor('beganAt') && <span className="field-error">{issueFor('beganAt')?.detail}</span>}
              </div>

              <div className="field">
                <label htmlFor="inc-source">How it reached us</label>
                <select
                  id="inc-source"
                  value={draft.source}
                  onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                >
                  {SOURCES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                {issueFor('source') && <span className="field-error">{issueFor('source')?.detail}</span>}
              </div>

              <div className="field">
                <label htmlFor="inc-severity">Severity, if it is known yet</label>
                <select
                  id="inc-severity"
                  value={draft.severity}
                  aria-describedby="inc-severity-hint"
                  onChange={(e) => setDraft({ ...draft, severity: e.target.value })}
                >
                  <option value="">Not judged yet</option>
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="inc-handler">Handled by</label>
                <input
                  id="inc-handler"
                  type="text"
                  value={draft.handledBy}
                  onChange={(e) => setDraft({ ...draft, handledBy: e.target.value })}
                />
              </div>

              <div className="field field-wide">
                <label htmlFor="inc-summary">What is known</label>
                <textarea
                  id="inc-summary"
                  rows={3}
                  value={draft.summary}
                  aria-describedby="inc-summary-hint"
                  onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                />
              </div>
            </div>

            <div className="profile-bar">
              <button type="submit" className="primary" data-action="tprm.incident,incidents.a01" disabled={submitting}>
                {submitting ? 'Recording' : 'Record it and show me the clocks'}
              </button>
              <button type="button" onClick={() => setFormOpen(false)} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="panel panel-flush" aria-labelledby="inc-open-head">
        <div className="panel-head">
          <h2 id="inc-open-head" style={{ margin: 0 }}>
            Open
          </h2>
        </div>
        <DataTable
          caption="Incidents that are open or contained, with what is owed on each."
          columns={incidentColumns(true, now)}
          rows={live}
          rowKey={(i) => i.id}
          defaultSort={{ key: 'discovered', dir: 'desc' }}
          empty={{
            title: 'No incident is open',
            detail: 'Nobody has recorded one, which is not the same as nothing having happened',
            action: { label: 'Open the register', href: '#/vendors' },
          }}
          footer={
            <ListCount shown={live.length} noun="open or contained" one="open or contained">
              , {closed.length} closed
              {closed.length >= CLOSED_PAGE ? ' among the ' + CLOSED_PAGE + ' most recently noticed' : ''}
            </ListCount>
          }
        />
      </section>

      {closed.length > 0 && (
        <section className="panel panel-flush" aria-labelledby="inc-closed-head">
          <div className="panel-head">
            <h2 id="inc-closed-head" style={{ margin: 0 }}>
              Closed
            </h2>
          </div>
          <DataTable
            caption="Incidents that have been closed, most recently noticed first."
            columns={incidentColumns(false, now)}
            rows={closed}
            rowKey={(i) => i.id}
            defaultSort={{ key: 'discovered', dir: 'desc' }}
            empty={{ title: 'Nothing has been closed', detail: '' }}
          />
        </section>
      )}
    </>
  )
}

function incidentColumns(withDuty: boolean, now: number): Column<Incident>[] {
  const columns: Column<Incident>[] = [
    {
      key: 'reference',
      header: 'Reference',
      sortValue: (i) => i.reference,
      render: (i) => (
        <>
          <a className="cell-primary mono" href={'#/incident/' + encodeURIComponent(i.id)}>
            {i.reference}
          </a>
          <span className="cell-sub">{sourceWords(i.source)}</span>
        </>
      ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (i) => i.vendorName ?? i.vendorCode ?? i.vendorId,
      render: (i) => (
        <>
          <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(i.vendorId)}>
            {i.vendorName || 'this vendor'}
          </a>
          <span className="cell-sub mono">{i.vendorCode || i.vendorId}</span>
        </>
      ),
    },
    {
      key: 'discovered',
      header: 'Noticed',
      sortValue: (i) => i.discoveredAt,
      render: (i) => (
        <>
          <span className="mono">{stampWithZone(i.discoveredAt)}</span>
          {i.beganAt && <span className="cell-sub">believed to have begun {stampWithZone(i.beganAt)}</span>}
        </>
      ),
    },
  ]

  if (withDuty) {
    columns.push({
      key: 'duty',
      header: 'What is owed',
      // Overdue first, then the nearest deadline, then the rows with nothing
      // running. The register is read from the top during an incident.
      sortValue: (i) => (i.clocks && i.clocks.overdue > 0 ? '0' : (i.clocks?.nextDueAt ?? null)),
      render: (i) => <DutyCell clocks={i.clocks} now={now} />,
    })
  }

  columns.push(
    {
      key: 'status',
      header: 'Status',
      sortValue: (i) => i.status,
      render: (i) => (
        <>
          <IncidentStatus status={i.status} />
          {i.severity && <span className="cell-sub">{i.severity.toLowerCase()}</span>}
        </>
      ),
    },
    {
      key: 'handler',
      header: 'Handled by',
      sortValue: (i) => i.handledBy ?? null,
      render: (i) =>
        i.handledBy ? <span className="mono">{i.handledBy}</span> : <span className="muted">nobody named</span>,
    },
  )

  return columns
}

// What is owed on one row: how many duties are engaged, how many are past their
// deadline, whether anything is owed now with no window at all, and when the
// next one falls. They stay four separate facts, because collapsing them would
// put a deadline against a duty the instrument owes immediately.
function DutyCell({ clocks, now }: { clocks: IncidentClocks | undefined; now: number }): JSX.Element {
  if (!clocks) {
    return <span className="muted">not worked out on this page, open the incident</span>
  }
  if (clocks.applicable === 0) {
    return <span className="muted">no duty engaged by the declared facts</span>
  }
  const remaining = clocks.nextDueAt ? Date.parse(clocks.nextDueAt) - now : null
  return (
    <>
      {/* Two labels, because they are two different duties: one missed a
          deadline, the other never had one. The column is "What is owed", so
          "Without delay" reads as the instrument's own words. */}
      {(clocks.overdue > 0 || clocks.owedWithoutDelay > 0) && (
        <span className="sw-row">
          {clocks.overdue > 0 && (
            <Status tone="crit" title={clocks.overdue + ' past the deadline'}>
              {clocks.overdue} overdue
            </Status>
          )}
          {clocks.owedWithoutDelay > 0 && (
            <Status tone="crit" title="Owed without delay">
              Without delay
            </Status>
          )}
        </span>
      )}
      {clocks.nextDue && remaining !== null && (
        <span className="cell-sub">
          {clocks.nextDue}, {countdownWords(remaining)}
        </span>
      )}
      {clocks.nextDueAt && (
        // The deadline itself, beside the countdown. A countdown is the thing
        // somebody acts on and the thing that goes stale; the stamp is what
        // they can check it against without reloading.
        <span className="cell-sub mono">{stampWithZone(clocks.nextDueAt)}</span>
      )}
      <span className="cell-sub">
        {clocks.applicable} {clocks.applicable === 1 ? 'duty' : 'duties'} engaged in all
      </span>
    </>
  )
}

// --------------------------------------------------------- one incident

function IncidentAnswer({ incidentId }: { incidentId: string }): JSX.Element {
  const [exposure, setExposure] = useState<Exposure | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const compute = useCallback(() => {
    setBusy(true)
    api
      .incidentExposure(incidentId)
      .then((e) => {
        setExposure(e)
        setError(null)
      })
      .catch(setError)
      .finally(() => setBusy(false))
  }, [incidentId])

  useEffect(() => {
    setExposure(null)
    setError(null)
    compute()
  }, [incidentId, compute])

  // The record travels inside the answer. There is no route that reads an
  // incident on its own and there does not need to be, because the only reason
  // to open one is to see what it exposed. The cost is that a refused traversal
  // leaves the header saying "Incident" with the refusal under it, which is
  // honest about what happened and says where to go next.
  const incident = exposure?.incident ?? null

  return (
    <>
      <div className="page-head">
        <div>
          <Crumbs className="kit-crumbs" home={{ label: 'Incidents', href: '#/incidents' }} current={incident?.reference ?? 'Incident'} />
          <h1 style={{ margin: 0 }}>{incident?.reference ?? 'Incident'}</h1>
        </div>
        {incident && <IncidentStatus status={incident.status} />}
      </div>

      {incident && <IncidentRecord incident={incident} onChanged={compute} />}

      {error != null && <ExposureLoadError error={error} />}
      {exposure == null && error == null && <Loading what="what this incident exposes" />}
      {exposure != null && (
        <ExposureAnswer
          exposure={exposure}
          onRecompute={compute}
          busy={busy}
          onTell={
            incident && can(HANDLE_INCIDENT)
              ? async (code, input) => {
                  await api.recordDutyTold(incident.id, code, input)
                  compute()
                }
              : undefined
          }
        />
      )}
    </>
  )
}

// The record itself, and the two things anybody changes about it while it runs:
// who is holding it, and where it has got to. Both are on this screen rather
// than on one of their own, because the person who has just read the clocks is
// the person who then says it is contained.
function IncidentRecord({ incident, onChanged }: { incident: Incident; onChanged: () => void }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState(incident.status)
  const [severity, setSeverity] = useState(incident.severity ?? '')
  const [handledBy, setHandledBy] = useState(incident.handledBy ?? '')
  const [closureNote, setClosureNote] = useState(incident.closureNote ?? '')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<unknown>(null)
  const [said, setSaid] = useState('')

  const mayHandle = can(HANDLE_INCIDENT)
  const closing = status === 'CLOSED'

  // The record changes under this form: saving it recomputes the answer, and
  // the incident that comes back is the one the server now holds. The controls
  // follow it whenever the form is shut, so opening it a second time offers
  // what is on the record rather than what somebody typed the first time.
  // While it is open they are left alone, because losing half a closure note to
  // a background refresh is worse than a stale select.
  useEffect(() => {
    if (open) return
    setStatus(incident.status)
    setSeverity(incident.severity ?? '')
    setHandledBy(incident.handledBy ?? '')
    setClosureNote(incident.closureNote ?? '')
  }, [open, incident.status, incident.severity, incident.handledBy, incident.closureNote])

  function save(ev: FormEvent) {
    ev.preventDefault()
    setSaveError(null)
    // The refusal says what to do; why a closure needs words sits in the (i)
    // beside the closure field, where it is read before anybody is refused.
    if (closing && closureNote.trim() === '') {
      setSaveError(new Error('Say what was concluded before closing this incident'))
      focusField('inc-closure')
      return
    }
    setSaving(true)
    api
      .changeIncident(incident.id, {
        status,
        severity: severity || undefined,
        handledBy: handledBy.trim() || undefined,
        closureNote: closing ? closureNote.trim() : undefined,
      })
      .then((reply) => {
        setSaid(reply.note)
        setOpen(false)
        onChanged()
      })
      .catch(setSaveError)
      .finally(() => setSaving(false))
  }

  return (
    <section className="panel" aria-labelledby="inc-record-head">
      <div className="page-head">
        <h2 id="inc-record-head" style={{ margin: 0, fontSize: 15 }}>
          The record
        </h2>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          disabled={!mayHandle}
          title={mayHandle ? undefined : refusalReason(HANDLE_INCIDENT)}
        >
          {open ? 'Leave it as it is' : 'Update where this has got to'}
        </button>
      </div>

      {said && (
        <p className="note sw-row" role="status" style={{ marginBottom: 10 }}>
          <NoteLine label="What the update recorded" text={said} />
        </p>
      )}

      <dl className="kv">
        <dt>Noticed</dt>
        <dd className="mono">{stampWithZone(incident.discoveredAt)}</dd>
        <dt>Believed to have begun</dt>
        <dd>
          {incident.beganAt ? (
            <span className="mono">{stampWithZone(incident.beganAt)}</span>
          ) : (
            <span className="muted">not established</span>
          )}
        </dd>
        <dt>How it reached us</dt>
        <dd>{sourceWords(incident.source)}</dd>
        <dt>Severity</dt>
        <dd>{incident.severity ?? <span className="muted">not judged</span>}</dd>
        <dt>Handled by</dt>
        <dd>{incident.handledBy ?? <span className="muted">nobody named</span>}</dd>
        <dt>What is known</dt>
        <dd>{incident.summary || <span className="muted">nothing recorded</span>}</dd>
        {incident.closedAt && (
          <>
            <dt>Closed</dt>
            <dd className="mono">{stampWithZone(incident.closedAt)}</dd>
            <dt>On what basis</dt>
            <dd>{incident.closureNote}</dd>
          </>
        )}
      </dl>

      {open && mayHandle && (
        <form onSubmit={save} style={{ marginTop: 12 }}>
          {saveError != null && <ErrorBox error={saveError} what="The incident" verb="updated" />}
          <div className="form-grid">
            <div className="field">
              <label htmlFor="inc-status">Where it has got to</label>
              <select
                id="inc-status"
                value={status}
                aria-describedby="inc-status-hint"
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <span className="hint" id="inc-status-hint">
                {STATUSES.find((s) => s.value === status)?.help}
              </span>
            </div>

            <div className="field">
              <label htmlFor="inc-sev">Severity</label>
              <select id="inc-sev" value={severity} onChange={(e) => setSeverity(e.target.value)}>
                <option value="">Not judged yet</option>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="inc-handled">Handled by</label>
              <input id="inc-handled" type="text" value={handledBy} onChange={(e) => setHandledBy(e.target.value)} />
            </div>

            {closing && (
              <div className="field field-wide">
                <span className="sw-title">
                  <label htmlFor="inc-closure">What was concluded</label>
                  <Toggletip label="Why a closure needs words">
                    An incident closed with no words is a record that somebody stopped looking, not that anything was
                    settled.
                  </Toggletip>
                </span>
                <textarea
                  id="inc-closure"
                  rows={3}
                  value={closureNote}
                  aria-describedby="inc-closure-hint"
                  onChange={(e) => setClosureNote(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="profile-bar">
            <button type="submit" className="primary" data-action="tprm.incident,incidents.a03" disabled={saving}>
              {saving ? 'Saving' : 'Save the record'}
            </button>
          </div>
        </form>
      )}

    </section>
  )
}
